import { BTLocalStorage } from "types/btStorage";

const MFA_NUDGE_STORAGE_KEY = "bt-object-mfaNudgeDismissal" as const;

const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

export interface IMfaNudgeEntry {
    userId: number;
    checkedAt: number;
    wasShown: boolean;
    dismissed: boolean;
    graceDaysRemaining: number;
    /**
     * The `forceMfa` value observed when this marker was written. Distinguishes a "checked, nothing
     * needed" user from a "checked, but blockable" user so the MFA block screen can safely skip its
     * GET only for provably non-blockable users. Absent on legacy markers written before this field
     * existed — treated as not block-safe (the block re-checks) to fail safe.
     */
    forceMfa?: boolean;
}

type MfaNudgeMap = Record<number, IMfaNudgeEntry>;

function isValidEntry(entry: IMfaNudgeEntry): boolean {
    return typeof entry.checkedAt === "number" && Date.now() - entry.checkedAt < TWELVE_HOURS_MS;
}

/**
 * Reads the stored per-user nudge map, dropping any entry whose 12h window has expired or that
 * lacks a numeric `checkedAt` (legacy shape self-heal — costs one extra GET per existing user
 * post-deploy).
 */
function readPrunedMap(): MfaNudgeMap {
    const stored = BTLocalStorage.get(MFA_NUDGE_STORAGE_KEY);
    if (!stored || typeof stored !== "object") return {};

    const pruned: MfaNudgeMap = {};
    for (const value of Object.values(stored as Record<string, unknown>)) {
        if (
            value &&
            typeof value === "object" &&
            typeof (value as IMfaNudgeEntry).userId === "number" &&
            isValidEntry(value as IMfaNudgeEntry)
        ) {
            const entry = value as IMfaNudgeEntry;
            pruned[entry.userId] = entry;
        }
    }
    return pruned;
}

export function getMfaNudgeStoredState(userId: number | null): IMfaNudgeEntry | null {
    if (userId == null) return null;
    const map = readPrunedMap();
    const entry = map[userId];
    if (!entry || !isValidEntry(entry)) return null;
    return entry;
}

export function markMfaNudgeShown(graceDaysRemaining: number, userId: number): void {
    const map = readPrunedMap();
    map[userId] = {
        userId,
        checkedAt: Date.now(),
        wasShown: true,
        dismissed: false,
        graceDaysRemaining,
        // A nudge is only shown when the server did NOT force MFA (shouldNudge is suppressed once
        // forceMfa is true), so a shown marker is provably block-safe.
        forceMfa: false,
    };
    BTLocalStorage.set(MFA_NUDGE_STORAGE_KEY, map);
}

export function markMfaNudgeNotNeeded(userId: number, forceMfa: boolean): void {
    const map = readPrunedMap();
    map[userId] = {
        userId,
        checkedAt: Date.now(),
        wasShown: false,
        dismissed: false,
        graceDaysRemaining: 0,
        forceMfa,
    };
    BTLocalStorage.set(MFA_NUDGE_STORAGE_KEY, map);
}

export function dismissMfaNudge(userId: number): void {
    const map = readPrunedMap();
    const existing = map[userId];
    map[userId] = {
        userId,
        checkedAt: Date.now(),
        wasShown: existing?.wasShown ?? true,
        dismissed: true,
        graceDaysRemaining: existing?.graceDaysRemaining ?? 0,
        forceMfa: existing?.forceMfa ?? false,
    };
    BTLocalStorage.set(MFA_NUDGE_STORAGE_KEY, map);
}

/**
 * True only when a valid (within-window) marker exists for the user that proves they are NOT
 * subject to a force-MFA block (`forceMfa === false`). Used to let the MFA block screen skip its
 * GET on reload without ever suppressing a legitimate block: any blockable or unprovable state
 * (forceMfa true, forceMfa absent on a legacy marker, expired, or no marker) returns false so the
 * block re-checks against fresh server state.
 */
export function hasBlockSafeMarker(userId: number | null): boolean {
    const entry = getMfaNudgeStoredState(userId);
    return entry?.forceMfa === false;
}
